import { useEffect, useState, useContext } from "react";
import {
  useNavigate,
  Link,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Table, Tooltip } from "antd";
import { Globe, Briefcase, MessageCircle } from "lucide-react";

import { PAGE_LENGTH, subscriptionPlans } from "../../config";
import defaultLogo from "../../assets/images/default_company_logo.jpg";
import { selectedRow } from "../../actionCreator/companyListingActionCreater";
import Loader from "../loader";
import { getPartialPhoneNumber } from "../../utils/utils";
import { getCompanyLogoUrl } from "../../utils";
import { AuthContext } from "../../context/AuthContext";
import TrialModal from "../../common/TrialModal";
import useUpgradeAlert from "../../common/hooks/useUpgradeAlert";
import CustomPagination from "../../common/CustomPagination";

const CompanyContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const showUpgradeAlert = useUpgradeAlert();
  const [searchParams] = useSearchParams();
  const { isAuthenticated: isLoggedIn, subscription } = useContext(AuthContext);
  const [companyList, setCompanyList] = useState();
  const [showModal, setShowModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const companyFilterList = useSelector((state) => state.companyListingReducer);
  const loading = useSelector((state) => state.CommonReducer.loading);
  const selectedRecords = useSelector(
    (state) => state.companyListingReducer.selectedRecords,
  );
  const companySelectionList = useSelector(
    (state) => state.companyListingReducer?.companySelectionList,
  );

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  useEffect(() => {
    if (companySelectionList?.length) {
      let selectionKeys = [];
      companySelectionList?.forEach((record) => {
        selectionKeys.push(record);
      });
      dispatch(selectedRow(selectionKeys));
    } else {
      dispatch(selectedRow([]));
    }
  }, [dispatch, companySelectionList]);

  const renderSocialLinks = (socialLinks) => {
    return (
      <div className="tw-flex tw-items-center tw-gap-2">
        {socialLinks?.map((link) => {
          if (link?.name === "facebook") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#1877F2] tw-transition-colors tw-duration-200"
                title="Facebook"
              >
                <Globe size={18} />
              </Link>
            );
          } else if (link?.name === "Linkedin") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#0A66C2] tw-transition-colors tw-duration-200"
                title="LinkedIn"
              >
                <Briefcase size={18} />
              </Link>
            );
          } else if (link?.name === "twitter") {
            return (
              <Link
                key={link?.proifileUrl}
                to={link?.proifileUrl}
                target="_blank"
                className="tw-text-slate-400 hover:tw-text-[#1DA1F2] tw-transition-colors tw-duration-200"
                title="Twitter"
              >
                <MessageCircle size={18} />
              </Link>
            );
          }
          return null;
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!companyFilterList?.companyList) return;
    const data =
      companyFilterList?.companyList?.map((record) => ({
        key: record.id,
        name: record,
        industry: record?.industry?.name,
        location: `${record?.city ? record.city + ", " : ""}${record?.state || ""}`,
        totalEmployees: record?.totalEmployees,
        phone: record.phoneNo,
        social: renderSocialLinks(record?.socialLinks),
      })) || [];

    setCompanyList(data);
  }, [companyFilterList]);

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => d !== record.key,
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, record.key]));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = changeRows?.map((d) => d.key);
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => !keys.includes(d),
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, ...keys]));
      }
    },
    getCheckboxProps: (record) => {
      return {
        disabled: record.name === "Disabled User", // Column configuration not to be checked
        name: record.name,
      };
    },
    selectedRowKeys: selectedRecords,
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      if (
        [subscriptionPlans?.trail].includes(
          subscription?.subscriptionPackage?.name,
        )
      ) {
        showUpgradeAlert("this page");
      } else {
        setShowModal(false);
      }
    } else {
      setShowModal(true);
    }
  };

  const onPageChange = (page, pageSize) => {
    checkLoginStatus();
    if (!isLoggedIn) {
      if (page > 1 || pageSize > PAGE_LENGTH)
        setOpenModal({ info: null, open: true });
      setShowModal(false);
      return false;
    } else {
      if (
        ![subscriptionPlans?.trail].includes(
          subscription?.subscriptionPackage?.name,
        )
      ) {
        searchParams.set("page", page);
        searchParams.set("size", pageSize);
        navigate(`${location.pathname}?${searchParams.toString()}`);
      }
    }
  };

  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-").toLowerCase();
    navigate(`/company-summary/${id}/${cname}`);
  };

  const columns = [
    {
      title: <div className="companyname">Company Name</div>,
      dataIndex: "name",
      render: (record, row) => {
        return (
          <div
            className="namecol"
            onClick={() => getDetails(row.key, record?.name)}
          >
            <div className="logo">
              <img
                src={getCompanyLogoUrl(record?.companyLogoUrl) || defaultLogo}
                alt={record?.name || "Company logo"}
                onError={(e) => {
                  e.currentTarget.src = defaultLogo;
                }}
              />
            </div>
            <Tooltip title={record?.name}>
              <span className="tw-truncate tw-w-full">{record?.name}</span>
            </Tooltip>
          </div>
        );
      },
      fixed: "left",
    },
    {
      title: "Industry",
      dataIndex: "industry",
      render: (record) => {
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center">
            <Tooltip title={record}>
              <span className="tw-truncate tw-w-full"> {record}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Location",
      dataIndex: "location",
      className: "location",
      render: (record) => {
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between">
            <Tooltip title={record}>
              <span className="tw-truncate tw-w-full">{record}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Employees",
      dataIndex: "totalEmployees",
      className: "totalEmployees",
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      render: (text, row) => {
        const number = getPartialPhoneNumber(text);
        return (
          <span className="tw-whitespace-nowrap tw-flex tw-items-center tw-justify-between">
            <Tooltip title={number}>
              <span className="tw-truncate tw-w-full">{number}</span>
            </Tooltip>
          </span>
        );
      },
    },
    {
      title: "Social",
      dataIndex: "social",
    },
  ];

  return (
    <>
      <div id="content-wrapper" className="tw-flex tw-flex-col">
        <div id="content">
          <div className="tw-pl-0 tw-pr-0">
            <div className="text-nowrap">
              {!!loading ? (
                <Loader />
              ) : (
                <div className="tw-custom-table-container tw-h-[calc(100vh-215px)] tw-flex tw-flex-col">
                  <Table
                    rowSelection={{
                      type: "checkbox",
                      ...rowSelection,
                    }}
                    columns={columns?.map((col) => ({
                      ...col,
                      onCell: () => ({
                        className: "word-break-keep-all",
                        ...(col?.dataIndex !== "name"
                          ? { style: { verticalAlign: "middle" } }
                          : {}),
                      }),
                    }))}
                    tableLayout="fixed"
                    scroll={false}
                    dataSource={companyList}
                    className="tw-custom-table tw-company-search-table"
                    pagination={false}
                  />
                  <CustomPagination
                    total={companyFilterList?.totalCount}
                    current={searchParams.get("page") || 1}
                    pageSize={
                      parseInt(PAGE_LENGTH) >
                      parseInt(companyFilterList?.totalCount)
                        ? companyFilterList?.totalCount
                        : searchParams.get("size")
                          ? searchParams.get("size")
                          : PAGE_LENGTH
                    }
                    onChange={onPageChange}
                    noBorder={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <TrialModal
        openModal={showModal}
        closeModal={closeModal}
        redirectToSignup={redirectToSignup}
        redirect={true}
      />
      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          isSubscriptionMissing
        />
      )}
    </>
  );
};

export default CompanyContent;
